import { useTranslation } from 'react-i18next';
import {
    useAppSelector,
    useTheme,
    Skeleton,
    Typography,
    Box,
} from '../../../../base';
import { reportsSelectors } from '../../../../actions';
import { getPyramidLevels, getPyramidColor, isJobView } from '../utils/funnelDataUtils';
import {
    StyledConversionCard,
    StyledPyramidContainer,
    StyledPyramidLevel,
} from './StageConversionRates.styles';

const StageConversionRates = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const funnelData = useAppSelector(reportsSelectors.getFunnelData);
    const isLoading = useAppSelector(reportsSelectors.getIsLoadingFunnel);

    const pyramidLevels = getPyramidLevels(funnelData);

    const renderSkeletons = () => (
        <>
            {[100, 75, 50].map((width, index) => (
                <Skeleton
                    key={index}
                    variant="rectangular"
                    width={`${width}%`}
                    height={80}
                    sx={{ borderRadius: 1 }}
                />
            ))}
        </>
    );

    const renderPyramid = () => {
        if (!funnelData || pyramidLevels.length === 0) return null;

        return (
            <Box
                sx={{
                    width: '100%',
                    maxHeight: pyramidLevels.length > 6 ? 500 : 'auto',
                    overflowY: pyramidLevels.length > 6 ? 'auto' : 'visible',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                {pyramidLevels.map((level, index) => (
                    <StyledPyramidLevel
                        key={`${level.label}-${index}`}
                        bgColor={getPyramidColor(index, pyramidLevels.length)}
                        widthPercent={level.widthPercent}
                        clipInset={level.clipInset}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme.palette.common.white,
                                fontWeight: 700,
                                lineHeight: 1.2,
                            }}
                        >
                            {level.count}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.common.white,
                                fontWeight: 600,
                                textAlign: 'center',
                                maxWidth: '90%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {level.labelKey ? t(level.labelKey) : level.label}
                        </Typography>
                    </StyledPyramidLevel>
                ))}
            </Box>
        );
    };

    const getSubtitle = () => {
        if (isJobView(funnelData)) {
            return t('reportsPage.stageConversionRates.jobViewSubtitle');
        }
        return t('reportsPage.stageConversionRates.subtitle');
    };

    return (
        <StyledConversionCard>
            <Typography variant="h6">
                {t('reportsPage.stageConversionRates.title')}
            </Typography>
            <Typography variant="body1" color={theme.palette.grey[500]}>
                {getSubtitle()}
            </Typography>
            <StyledPyramidContainer spacing={2}>
                {isLoading ? renderSkeletons() : renderPyramid()}
            </StyledPyramidContainer>
        </StyledConversionCard>
    );
};

export default StageConversionRates;
